import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getMonthDate} from 'components/Accommodation/MonthlyPriceTrend/utils'
import {useShowHotelDatePickerOverlay} from 'hooks/useShowOverlay/useShowOverlay'
import {getCurrencyCode, getTaxesIncluded} from 'modules/meta/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {DateGuestPickerSource} from 'modules/overlay/types'
import {getHotelAverageRates} from 'modules/seoApi/selectors'

import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Team} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'

const PriceColumn = styled.div`
  max-width: 55%;
`

const StyledButton = styled(Button)`
  max-width: 50%;
`

export enum DatelessCTAVariant {
  DesktopOfferBoxCTA,
  DesktopStickyHeader,
  DesktopHeader,
  RoomListing,
  OfferBoxCTA,
  BottomCTA
}

interface DatelessCTAProps {
  variant: DatelessCTAVariant
  priceFrom?: number
  hotelId?: string
}

type PriceSize = 'lg' | 'xl'
type CaptionVariant = 'bodyXS' | 'bodyS'

/**
 * How the "with taxes & fees" line sits next to the average rate caption: on
 * the same line, on its own line inside the caption, or as a separate block.
 */
type TaxesLine = 'none' | 'inline' | 'newline' | 'block'

interface PriceProps {
  averageRate?: number
  currentMonth?: string
  priceFrom?: number
  size: PriceSize
  captionVariant: CaptionVariant
  taxesLine: TaxesLine
}

/**
 * The average rate for the current month when we have it, the cheapest offer
 * price otherwise. Renders nothing when we have neither.
 */
const DatelessPrice = ({
  averageRate,
  currentMonth,
  priceFrom,
  size,
  captionVariant,
  taxesLine
}: PriceProps) => {
  const currencyCode = useSelector(getCurrencyCode)

  if (averageRate) {
    return (
      <>
        <PriceComposition
          size={size}
          anchorPrice={undefined}
          currencyCode={currencyCode}
          price={averageRate}
          showIndication
        />
        <Text variant={captionVariant}>
          <FormattedMessageWrapper
            id="averageForMonth"
            defaultMessage="Avg. for {month}"
            values={{month: currentMonth}}
          />
          {taxesLine === 'inline' && (
            <>
              {' '}
              <FormattedMessageWrapper
                id="taxation.withTaxesAndFees"
                defaultMessage="with taxes & fees"
              />
            </>
          )}
          {taxesLine === 'newline' && (
            <>
              <br />
              <FormattedMessageWrapper
                id="taxation.withTaxesAndFees"
                defaultMessage="with taxes & fees"
              />
            </>
          )}
        </Text>
        {taxesLine === 'block' && (
          <Text variant={captionVariant}>
            <FormattedMessageWrapper
              id="taxation.withTaxesAndFees"
              defaultMessage="with taxes & fees"
            />
          </Text>
        )}
      </>
    )
  }

  if (!priceFrom) return null

  return (
    <>
      <Text variant={captionVariant}>
        <FormattedMessageWrapper id="From" defaultMessage="From" />
      </Text>
      <PriceComposition
        size={size}
        anchorPrice={undefined}
        currencyCode={currencyCode}
        price={priceFrom}
        showIndication
      />
    </>
  )
}

const useCurrentMonthAverageRate = (
  hotelId: string | undefined,
  showTotalPrices: boolean
) => {
  const {formatDate} = useIntl()
  const averageRates = useSelector(state =>
    getHotelAverageRates(state, hotelId)
  )

  return useMemo(() => {
    const currentMonthNumber = new Date().getMonth() + 1
    const currentMonthAverageRate = averageRates?.monthlyAverage?.find(
      rate => rate.month === currentMonthNumber
    )

    if (!currentMonthAverageRate) {
      return {averageRate: undefined, currentMonth: undefined}
    }

    return {
      averageRate: showTotalPrices
        ? currentMonthAverageRate.baseRate + currentMonthAverageRate.taxesFees
        : currentMonthAverageRate.baseRate,
      currentMonth: formatDate(getMonthDate(currentMonthNumber), {
        month: 'long'
      })
    }
  }, [averageRates, showTotalPrices, formatDate])
}

export const DatelessCTA = ({
  variant,
  priceFrom,
  hotelId
}: DatelessCTAProps) => {
  const showHotelDatePickerOverlay = useShowHotelDatePickerOverlay()
  const showTotalPrices = useSelector(getTaxesIncluded)
  const dispatch = useDispatch()
  const {isDesktopXs} = useDeviceLayout()

  const {averageRate, currentMonth} = useCurrentMonthAverageRate(
    hotelId,
    showTotalPrices
  )

  const openDatePicker = () => {
    if (
      variant === DatelessCTAVariant.DesktopStickyHeader ||
      (variant === DatelessCTAVariant.RoomListing && isDesktopXs)
    ) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }

    if (variant === DatelessCTAVariant.RoomListing) {
      dispatch(hideOverlay())
      setTimeout(() => {
        showHotelDatePickerOverlay(
          DatePickerType.CheckIn,
          Team.Default,
          DateGuestPickerSource.AccommodationPageSearch
        )
      }, 0)

      return
    }

    showHotelDatePickerOverlay(
      DatePickerType.CheckIn,
      Team.Default,
      DateGuestPickerSource.AccommodationPageSearch
    )
  }

  const priceProps = {averageRate, currentMonth, priceFrom}

  switch (variant) {
    case DatelessCTAVariant.RoomListing:
      return (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={openDatePicker}
          dataId="datelessRoomCTA"
        >
          <FormattedMessageWrapper
            id="checkAvailability"
            defaultMessage="Check availability"
          />
        </Button>
      )

    case DatelessCTAVariant.DesktopStickyHeader:
      return (
        <Button variant="primary" size="lg" onClick={openDatePicker}>
          <FormattedMessageWrapper
            id="datePicker.selectDates"
            defaultMessage="Select dates"
          />
        </Button>
      )

    case DatelessCTAVariant.DesktopHeader:
      return (
        <Box
          display="flex"
          alignItems="center"
          gap="s500"
          paddingTop="s250"
          paddingBottom="s250"
          flex="0 0 fit-content"
        >
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <DatelessPrice
              {...priceProps}
              size="lg"
              captionVariant="bodyXS"
              taxesLine="none"
            />
          </Box>
          <Box paddingTop="s250" paddingBottom="s250">
            <Button variant="primary" size="xl" onClick={openDatePicker}>
              <FormattedMessageWrapper
                id="datePicker.selectDates"
                defaultMessage="Select dates"
              />
            </Button>
          </Box>
        </Box>
      )

    case DatelessCTAVariant.DesktopOfferBoxCTA:
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="s500"
        >
          <div>
            <DatelessPrice
              {...priceProps}
              size="xl"
              captionVariant="bodyS"
              taxesLine={showTotalPrices ? 'block' : 'none'}
            />
          </div>
          <Button
            fullWidth
            variant="primary"
            size="lg"
            onClick={openDatePicker}
          >
            <FormattedMessageWrapper
              id="datePicker.selectDates"
              defaultMessage="Select dates"
            />
          </Button>
        </Box>
      )

    default:
      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          padding="s500"
          gap="s400"
          justifyContent="space-between"
        >
          <PriceColumn>
            <DatelessPrice
              {...priceProps}
              size="lg"
              captionVariant="bodyXS"
              taxesLine={
                !showTotalPrices
                  ? 'none'
                  : variant === DatelessCTAVariant.BottomCTA
                    ? 'inline'
                    : 'newline'
              }
            />
          </PriceColumn>
          <StyledButton variant="primary" size="xl" onClick={openDatePicker}>
            <FormattedMessageWrapper
              id="datePicker.selectDates"
              defaultMessage="Select dates"
            />
          </StyledButton>
        </Box>
      )
  }
}
